/**
 * The browser transport: the platform's own `fetch`, and nothing else.
 *
 * This is the implementation that has always been in use — the modules above
 * called `fetch` directly until now. Routing them through here changes no
 * behaviour, which is the point: the 54 tests that pass unchanged are the
 * evidence that introducing the seam was a move rather than a rewrite.
 *
 * See `net.types.ts` for why the contract is fetch-shaped.
 */

import type { ExternalAuthReturn, Stream, SubmissionSeed, Transport } from "./net.types";
import { isPublicSession } from "./publicSession";

/**
 * Read `globalThis.fetch` per call rather than binding it once.
 *
 * Two reasons, and both are load-bearing. `src/test/server.ts` installs a fake
 * daemon by assigning `globalThis.fetch` *after* these modules have loaded, so a
 * captured reference would be the real one and every test would try to reach the
 * network. And a service worker or an instrumentation shim may replace `fetch`
 * at any point during a session, which a captured reference would miss.
 */
export const request: Transport["request"] = (input, init) => globalThis.fetch(input, init);

/**
 * `EventSource`, which is the browser's whole answer to this.
 *
 * Moved from `lib/events.ts` unchanged, including the one piece of interpretation
 * that has to happen here: `readyState`. Per the HTML spec a stream whose request
 * returned a non-200 fails *permanently* — `CLOSED`, one `error`, and the browser
 * never retries — whereas a failure below HTTP leaves it `CONNECTING` and the
 * browser retrying by itself, carrying a `Last-Event-ID` that nothing above could
 * reproduce. That difference is the whole of `answered`, and reading it is the
 * only thing this function knows that the shared policy does not.
 *
 * The listener is `addEventListener("state", ...)` and not `onmessage`, because
 * the daemon names the event. `onmessage` fires only for an unnamed event and
 * would never fire here — which is the single most likely way to build this,
 * watch the network tab show a healthy stream, and have the app silently do
 * nothing.
 */
export const openStream: Transport["openStream"] = (url, handlers) => {
  const es = new EventSource(url);
  es.addEventListener("open", () => handlers.onOpen());
  es.addEventListener("state", () => handlers.onState());
  es.addEventListener("error", () => handlers.onError(es.readyState === EventSource.CLOSED));
  // `retries`: an `EventSource` whose connection dies below HTTP goes back to
  // `CONNECTING` and redials by itself, with `Last-Event-ID`. See the contract
  // for why the policy asks rather than assumes.
  const stream: Stream = { close: () => es.close(), retries: true };
  return stream;
};

/**
 * Hand bytes to the browser's download machinery.
 *
 * An object URL and a synthetic click, because there is nothing else — a page
 * cannot write a file. Note what this deliberately is not: a plain `<a href>` to
 * the blob endpoint. A cookie session would survive that, since the browser
 * attaches it itself, but a device token would not — HydraMail authenticates
 * that endpoint by header and never by `?access_token=`, and a link cannot carry
 * a header. One path that works for both credentials beats two that differ by
 * which one happens to be in use. There is no query-string fallback on purpose:
 * a token in a URL turns up in proxy logs and in the referrer of anything the
 * page loads next.
 */
export const saveFiles: Transport["saveFiles"] = async (files) => {
  // The browser has no folder prompt; each file goes to the usual downloads
  // place, one after another, the same as clicking each attachment.
  for (const f of files) {
    await saveFile(f.blob, f.name);
  }
};

export const saveFile: Transport["saveFile"] = async (bytes, filename) => {
  const url = URL.createObjectURL(bytes);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "attachment";
    a.click();
  } finally {
    // The click is synchronous; the fetch of the object URL it triggers is not,
    // so the revoke waits rather than racing the download.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
};

/**
 * Send this page to the daemon, which redirects to the provider.
 *
 * A navigation, not a `fetch`. The daemon answers with a 302 to
 * accounts.google.com, and a cross-origin redirect that a fetch follows sets no
 * CORS headers — so the fetch fails, and even if it succeeded, the consent
 * screen is a page a person has to look at. `assign` rather than `replace` so
 * the back button still returns here from Google's screen.
 */
export const startExternalAuth: Transport["startExternalAuth"] = (path) => {
  window.location.assign(path);
};

/**
 * Read the outcome out of the query string, and remove it.
 *
 * Delivered synchronously at subscribe time because in a browser the return leg
 * *is* this page load: the daemon sent us back to `HM_EXTERNAL_URL` with
 * `?oauth=connected&account=<name>`, or `?oauth=denied` if someone pressed
 * Cancel. There is no `return_to` anywhere in that flow, deliberately — a
 * destination taken from the request is an open redirect — so this is the one
 * place the client learns how it went.
 *
 * The query string is stripped with `replaceState` rather than left in place. A
 * reload would otherwise re-announce a connection that happened ten minutes ago,
 * and a URL copied out of the address bar would carry a stale outcome to
 * whoever it was sent to.
 */
export const onExternalAuthReturn: Transport["onExternalAuthReturn"] = (handler) => {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get("oauth");
  if (outcome !== "connected" && outcome !== "denied") return;

  const account = params.get("account");
  params.delete("oauth");
  params.delete("account");
  const query = params.toString();
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
  );
  const result: ExternalAuthReturn = { outcome, account };
  handler(result);
};

/**
 * No. A browser tab is not a detached view of this app; it is another copy.
 *
 * Everything a tab strip would offer is absent in this build as a result, and
 * absent rather than inert — see the contract for why the distinction is the
 * whole point of this being a constant rather than something inferred.
 */
export const multiWindow: Transport["multiWindow"] = false;

/**
 * Nothing. A page does not have a window it can move, size or close.
 *
 * `null` rather than an object whose methods reject, so the shared code cannot
 * render a control that would always fail -- and so the browser bundle has no
 * path to `@tauri-apps/api` at all. See the contract.
 */
export const windowChrome: Transport["windowChrome"] = null;

/**
 * Nothing to ask: the page and the API are the same origin.
 *
 * `null` rather than a getter returning `location.origin`, because the two are
 * different statements. This one says the question does not arise here, and it
 * is what keeps a "server address" field off a screen where changing it could
 * only break things.
 */
export const daemonAddress: Transport["daemonAddress"] = null;

/**
 * Always the mail.
 *
 * A browser has one page. A second tab of this app is a second whole app —
 * its own store, its own session, its own stream — rather than a detached view
 * of this one, which is exactly the arrangement tabs exist here to avoid.
 *
 * Note that this answering `"main"` says nothing about whether the build *has*
 * other kinds of window: the desktop main window answers `"main"` too. Read
 * {@link multiWindow} for that.
 */
export const windowRole: Transport["windowRole"] = () => "main";

/**
 * Refused, rather than opened as a browser tab.
 *
 * A `window.open` would look like the right answer and would be a different
 * feature: a second copy of the whole client at a URL, not a detached view. The
 * tab strip is absent in this build for the same reason the offline pane is —
 * see `retention` below — so nothing above should be calling this, and saying so
 * beats doing something plausible.
 */
export const openMessageWindow: Transport["openMessageWindow"] = () =>
  Promise.reject(new Error("this build has one window"));

/** Nothing to merge, for the same reason. */
export const mergeIntoMain: Transport["mergeIntoMain"] = () =>
  Promise.reject(new Error("this build has one window"));

/**
 * Refused, and the composer stays in the page — which is not a lesser version
 * of this feature, it is the browser's whole answer to it.
 *
 * A `window.open` would be a second copy of the client, with its own session,
 * its own store and its own idea of what the draft says, autosaving over the
 * first one's revisions. The in-page composer has none of those problems and is
 * what this build has always shown.
 *
 * Rejecting rather than resolving is what makes that happen: `lib/compose.ts`
 * composes in place when this fails, so the browser reaches the same composer
 * through the same code path the desktop uses when a window will not open. One
 * fallback, exercised on both builds, rather than a branch only one of them
 * takes.
 */
export const openComposeWindow: Transport["openComposeWindow"] = () =>
  Promise.reject(new Error("this build has one window"));

/** Nothing to close: the composer here is a layer, and `back` closes it. */
export const closeComposeWindow: Transport["closeComposeWindow"] = () =>
  Promise.reject(new Error("this build has one window"));

/**
 * Nothing. A page is never handed a link by the operating system.
 *
 * Not an oversight and not a gap to fill later: in a browser, a link naming a
 * message *is* the page load, so the fragment is read once at startup by
 * `lib/navigation.ts` and there is no second channel for one to arrive on. An
 * empty function rather than an absent one, so the shared code subscribes
 * without asking which build it is in.
 */
export const onMessageLink: Transport["onMessageLink"] = () => {};

/**
 * The page is the only window, so the announcement is a synchronous local
 * dispatch — which is exactly what the old direct `outbox.register` call was,
 * so the browser's undo strip behaves as it always has. The handler list is
 * subscribed at module load by `lib/outbox.ts`, before any send can happen.
 */
const submissionHandlers: ((seed: SubmissionSeed) => void)[] = [];

export const onSubmissionAnnounced: Transport["onSubmissionAnnounced"] = (handler) => {
  submissionHandlers.push(handler);
};

export const announceSubmission: Transport["announceSubmission"] = (seed) => {
  for (const handler of submissionHandlers) handler(seed);
  return Promise.resolve();
};

// A browser's notification buttons belong to the service worker, not the page.
export const onToastAction: Transport["onToastAction"] = () => {};

/**
 * Nothing. There is no replica here, so nothing destroys a row behind the
 * interface's back.
 *
 * A browser learns that a message is gone the same way it learns everything
 * else: the next query does not return it. There is no second bookkeeping that
 * could fall out of step with that, which is exactly what this callback exists
 * to keep in step on the desktop.
 */
export const onReplicated: Transport["onReplicated"] = () => {};

/**
 * The token, in the only place a page can put one.
 *
 * The key and the JSON encoding are exactly what `persisted()` in
 * `lib/settings.ts` used before this moved, and deliberately so: anyone already
 * holding a token has it under `hm.token` as a JSON string, and a change of
 * spelling here would read as being signed out by an update.
 */
const TOKEN_KEY = "hm.token";

export const credential: Transport["credential"] = {
  async load() {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (raw === null) return "";
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "string" ? parsed : "";
    } catch {
      // Private-browsing modes throw on access rather than returning null, and
      // a half-written value fails to parse. An app that forgets a token shows
      // a login screen; one that fails to start shows nothing.
      return "";
    }
  },
  async save(token) {
    try {
      // A public session never leaves a bearer on the machine. Removing rather
      // than skipping the write, which is the difference that matters here: the
      // key may already hold a token belonging to whoever used this browser
      // last, and "nothing is kept on this machine" has to cover what was
      // already there. Every path that ends a session -- signing in over a
      // cookie, signing out, the idle timer -- sets this signal to "" and
      // therefore comes through here.
      if (isPublicSession()) localStorage.removeItem(TOKEN_KEY);
      else localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    } catch {
      /* as above */
    }
  },
};

/**
 * The browser attaches the session cookie itself, on every same-origin request.
 * It is the credential here, which is what makes discarding a device token
 * after a successful login the right thing to do rather than a loss.
 */
export const carriesSessionCookie: Transport["carriesSessionCookie"] = true;

/**
 * Nothing to do. A page has no local copy of the mail to bring up to date.
 *
 * Deliberately an empty function rather than an absent one: the shared code
 * calls this after every event, and the whole point of the contract is that it
 * does not have to know which build it is running in.
 */
export const replicate: Transport["replicate"] = () => {};

/**
 * None. A page has nowhere to keep mail, so there is nothing to ration.
 *
 * `null` rather than an object whose methods report zero — see the field on
 * `Transport`. The settings screen draws no offline pane here, and the reading
 * pane offers no pin, because a pin button that silently does nothing is worse
 * than no pin button: it is a promise somebody would rely on in a tunnel.
 */
export const retention: Transport["retention"] = null;

/**
 * Never. A browser signs in at `/auth/login` and lets the cookie do the work.
 *
 * Refusing rather than quietly proxying, because a browser reaching for this
 * would mean something above the transport had stopped telling the two builds
 * apart -- and the daemon would refuse it anyway, less legibly.
 */
export const requestToken: Transport["requestToken"] = () =>
  Promise.reject(new Error("a browser signs in with a session cookie, not a token"));

/**
 * None. A page cannot be out of date: loading it is the update.
 *
 * `null` rather than a check that always answers "up to date", because those are
 * different claims. This one says the question does not arise, which is what
 * keeps a "check for updates" button off a screen where pressing it could only
 * ever confirm what reloading already did.
 *
 * The browser's About screen does offer the *desktop* build for download, and
 * that is deliberately not here: it is an authenticated GET for a JSON manifest,
 * which is an ordinary request rather than a capability of the platform. See
 * `lib/release.ts`.
 */
export const updates: Transport["updates"] = null;

/** Nothing: a browser tab is not an email app the OS can hand links to. */
export const defaultMailApp: Transport["defaultMailApp"] = null;

// Compile-time proof that this module satisfies the contract. A missing or
// wrongly-typed export fails here rather than at the first call site, and — more
// usefully — the desktop implementation is checked against the same interface,
// so the two builds cannot drift apart in shape without the build saying so.
/**
 * The browser's console is already the readable log, so the line goes there.
 * `console.debug` rather than `log`: it is diagnostics, filterable, and silent
 * in a default devtools view.
 */
export const report: Transport["report"] = (text) => {
  console.debug(`hm: ${text}`);
};

const _contract: Transport = {
  request,
  report,
  openStream,
  multiWindow,
  windowChrome,
  daemonAddress,
  windowRole,
  openMessageWindow,
  mergeIntoMain,
  openComposeWindow,
  closeComposeWindow,
  onMessageLink,
  announceSubmission,
  onSubmissionAnnounced,
  onToastAction,
  onReplicated,
  saveFile,
  saveFiles,
  startExternalAuth,
  onExternalAuthReturn,
  credential,
  carriesSessionCookie,
  replicate,
  requestToken,
  retention,
  updates,
  defaultMailApp,
};
void _contract;
